import { useEffect, useMemo, useState } from "react";
import { useConfirm } from "@/components/ConfirmProvider";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { devWarn } from "@/lib/log";
import Drawer, { FormField, FormTextarea, getSaveLabel } from "@/components/Drawer";
import { Plus, X } from "@phosphor-icons/react";
import { fmtMoney } from "@/lib/format";
import useBranchTypes from "@/hooks/useBranchTypes";

const EMPTY_PACKAGE = {
  name: "",
  price: "",
  description: "",
  items: [],
  active: true,
  igst_pct: 0,
  cgst_pct: 0,
  helmet_igst_pct: 0,
  helmet_cgst_pct: 0,
  accessories_igst_pct: 0,
  accessories_cgst_pct: 0,
  branch_type_id: "general",
  model_category: "",
  company_discount: 0,
  // Profit-Per-Vehicle margin master (going-forward reporting). All ₹.
  dealer_margin: 0,
  mandatory_margin: 0,
  helmet_margin: 0,
  add_acc_margin: 0,
};

// Common dealership model classifications. Stored upper-case; matched as a
// loose enum (admin can still type a custom value via the input fallback so a
// new category like "TROLLEY" can be introduced without a migration).
export const MODEL_CATEGORY_OPTIONS = [
  "SCOOTER",
  "MOPED",
  "MOTORCYCLE",
  "PREMIUM",
  "SUPER PREMIUM",
  "ELECTRIC",
  "COMMERCIAL",
  "OTHER",
];

const TAX_LABELS = new Set(["IGST", "CGST"]);
const EX_SHOWROOM_LABEL = "Ex-showroom (no tax)";

// Mirror of backend `_BUCKET_LABEL_MAP` in routers/packages.py — used to
// auto-suggest a sensible default bucket for known labels in the picker.
const BUCKET_DEFAULT_FOR_LABEL = {
  [EX_SHOWROOM_LABEL]: "ex_showroom",
  "Handling Charges": "ex_showroom",
  "Life Tax": "ex_showroom",
  "TR Charges": "ex_showroom",
  "Hypothecation": "ex_showroom",
  IGST: "ex_showroom",
  CGST: "ex_showroom",
  Helmet: "helmet",
  Accessories: "accessories",
  "Additional Accessories": "accessories",
};

const BUCKET_OPTIONS = [
  { v: "ex_showroom", l: "Ex-showroom" },
  { v: "helmet", l: "Helmet" },
  { v: "accessories", l: "Accessories" },
  { v: "other", l: "Other" },
];

function ItemRow({ item, idx, onUpdate, onRemove }) {
  const isAutoTax = TAX_LABELS.has(item.label);
  if (isAutoTax) {
    return (
      <li
        data-testid={`item-row-${idx}`}
        className="px-3 py-2 grid grid-cols-[1fr_110px_120px_110px_auto_auto] items-center gap-2 bg-canvas-panel"
      >
        <div className="text-sm text-ink-muted italic flex items-center gap-2">
          {item.label}
          <span className="font-mono text-[9px] uppercase tracking-wider text-ink-subtle">
            auto · {item.note || "computed"}
          </span>
        </div>
        <div className="px-2 py-1 font-mono text-sm text-ink">
          {fmtMoney(Number(item.price) || 0)}
        </div>
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">
          ex_showroom
        </span>
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">
          —
        </span>
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">
          mandatory
        </span>
        <span className="w-[18px]" />
      </li>
    );
  }
  return (
    <li
      data-testid={`item-row-${idx}`}
      className="px-3 py-2 grid grid-cols-[1fr_110px_120px_110px_auto_auto] items-center gap-2"
    >
      <input
        value={item.label}
        onChange={(e) => onUpdate(idx, { label: e.target.value })}
        data-testid={`item-label-${idx}`}
        className="px-2 py-1 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
      />
      <input
        type="number"
        value={item.price ?? 0}
        onChange={(e) => onUpdate(idx, { price: Number(e.target.value) || 0 })}
        data-testid={`item-price-${idx}`}
        className="px-2 py-1 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
        placeholder="₹"
      />
      <select
        value={item.receipt_bucket || "other"}
        onChange={(e) => onUpdate(idx, { receipt_bucket: e.target.value })}
        data-testid={`item-bucket-${idx}`}
        className="px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono focus:outline-none focus:border-ink"
        title="Which printed receipt this line belongs to"
      >
        {BUCKET_OPTIONS.map((opt) => (
          <option key={opt.v} value={opt.v}>
            {opt.l}
          </option>
        ))}
      </select>
      <input
        value={item.hsn_code || ""}
        onChange={(e) => onUpdate(idx, { hsn_code: e.target.value })}
        data-testid={`item-hsn-${idx}`}
        placeholder="HSN"
        title="HSN/SAC code (printed on tax invoice)"
        className="px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono focus:outline-none focus:border-ink"
      />
      <label className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-ink-muted cursor-pointer">
        <input
          type="checkbox"
          checked={!!item.optional}
          onChange={(e) => onUpdate(idx, { optional: e.target.checked })}
          data-testid={`item-optional-${idx}`}
          className="accent-ink"
        />
        Optional
      </label>
      <button
        type="button"
        onClick={() => onRemove(idx)}
        data-testid={`item-remove-${idx}`}
        className="p-1 hover:text-destructive"
        aria-label="Remove item"
      >
        <X size={12} weight="bold" />
      </button>
    </li>
  );
}

function NewItemForm({ onAdd }) {
  const [label, setLabel] = useState("");
  const [price, setPrice] = useState("");
  const [hsn, setHsn] = useState("");
  const [optional, setOptional] = useState(false);

  const submit = () => {
    if (!label.trim()) return;
    const trimmed = label.trim();
    const bucket = BUCKET_DEFAULT_FOR_LABEL[trimmed] || "other";
    onAdd({
      label: trimmed,
      price: Number(price) || 0,
      optional,
      note: "",
      receipt_bucket: bucket,
      hsn_code: hsn.trim(),
    });
    setLabel("");
    setPrice("");
    setHsn("");
    setOptional(false);
  };

  return (
    <div className="grid grid-cols-[1fr_110px_120px_110px_auto_auto] gap-2">
      <input
        data-testid="item-input"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="e.g., Insurance"
        className="px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
      />
      <input
        data-testid="item-price-input"
        type="number"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        placeholder="₹ Price"
        className="px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
      />
      <span className="text-[10px] font-mono uppercase tracking-wider text-ink-subtle self-center">
        bucket auto-set
      </span>
      <input
        data-testid="item-hsn-input"
        value={hsn}
        onChange={(e) => setHsn(e.target.value)}
        placeholder="HSN"
        title="HSN/SAC code (printed on tax invoice)"
        className="px-2 py-2 bg-canvas border border-canvas-line text-xs font-mono focus:outline-none focus:border-ink"
      />
      <label className="px-2 flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-ink-muted cursor-pointer">
        <input
          type="checkbox"
          checked={optional}
          onChange={(e) => setOptional(e.target.checked)}
          data-testid="item-optional-input"
          className="accent-ink"
        />
        Optional
      </label>
      <button
        type="button"
        onClick={submit}
        data-testid="item-add"
        className="px-3 bg-ink text-white text-sm font-semibold hover:bg-brand flex items-center gap-1"
      >
        <Plus size={12} weight="bold" />
        Add
      </button>
    </div>
  );
}

function ItemsEditor({ items, onChange }) {
  const handleAdd = (item) => onChange([...items, item]);

  const handleRemove = (idx) => {
    const next = [...items];
    next.splice(idx, 1);
    onChange(next);
  };

  const handleUpdate = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
        Items / Charges (priced)
      </label>
      <ul className="border border-canvas-line divide-y divide-canvas-line mb-2">
        {items.length === 0 && (
          <li className="px-3 py-3 font-mono text-[11px] text-ink-subtle text-center">
            No items yet. Add charges / add-ons one by one.
          </li>
        )}
        {items.map((it, idx) => (
          <ItemRow
            key={`${it.label}-${idx}`}
            item={it}
            idx={idx}
            onUpdate={handleUpdate}
            onRemove={handleRemove}
          />
        ))}
      </ul>
      <NewItemForm onAdd={handleAdd} />
    </div>
  );
}

function TaxFields({ exShowroom, igstPct, cgstPct, onChange }) {
  const igstAmount = exShowroom * (Number(igstPct) || 0) / 100;
  const cgstAmount = exShowroom * (Number(cgstPct) || 0) / 100;
  const invoiceCost = exShowroom + igstAmount + cgstAmount;
  return (
    <div className="border border-canvas-line p-3 space-y-3 bg-canvas-panel">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
        Vehicle Pricing (Tax)
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">
            IGST % <span className="text-ink-subtle normal-case">(inter-state)</span>
          </label>
          <div className="relative">
            <input
              type="number"
              step="0.01"
              value={igstPct ?? 0}
              onChange={(e) => onChange({ igst_pct: Number(e.target.value) || 0 })}
              data-testid="package-igst-pct"
              className="w-full px-3 py-2 pr-8 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-subtle text-xs">%</span>
          </div>
          <div className="mt-1 font-mono text-[10px] text-ink-muted">
            = {fmtMoney(igstAmount)}
          </div>
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1">
            CGST + SGST % <span className="text-ink-subtle normal-case">(intra, each leg)</span>
          </label>
          <div className="relative">
            <input
              type="number"
              step="0.01"
              value={cgstPct ?? 0}
              onChange={(e) => onChange({ cgst_pct: Number(e.target.value) || 0 })}
              data-testid="package-cgst-pct"
              className="w-full px-3 py-2 pr-8 bg-canvas border border-canvas-line text-sm font-mono focus:outline-none focus:border-ink"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-subtle text-xs">%</span>
          </div>
          <div className="mt-1 font-mono text-[10px] text-ink-muted">
            = {fmtMoney(cgstAmount)} <span className="text-ink-subtle">CGST · same as SGST</span>
          </div>
        </div>
      </div>
      <div className="border-t border-canvas-line pt-2 flex items-center justify-between font-mono text-xs">
        <span className="text-ink-muted">Vehicle Invoice Cost</span>
        <span data-testid="vehicle-invoice-cost" className="font-bold text-ink">
          {fmtMoney(invoiceCost)}
        </span>
      </div>
      <div className="font-mono text-[10px] text-ink-subtle leading-tight">
        For intra-state sales, both CGST and SGST will be charged at the rate above (each leg).
        For inter-state, only IGST applies. Ex-showroom (no tax) {fmtMoney(exShowroom)} is saved
        on the package; tax items are auto-injected as mandatory line items.
      </div>
    </div>
  );
}

function BucketGstRow({ label, igstPct, cgstPct, onChange, testidPrefix }) {
  return (
    <div className="grid grid-cols-[100px_1fr_1fr] items-center gap-2">
      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <div className="relative">
        <input
          type="number"
          step="0.01"
          value={igstPct ?? 0}
          onChange={(e) => onChange("igst", Number(e.target.value) || 0)}
          data-testid={`${testidPrefix}-igst`}
          placeholder="IGST %"
          className="w-full px-2 py-1.5 pr-12 bg-canvas border border-canvas-line text-xs font-mono focus:outline-none focus:border-ink"
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-subtle text-[10px]">
          IGST %
        </span>
      </div>
      <div className="relative">
        <input
          type="number"
          step="0.01"
          value={cgstPct ?? 0}
          onChange={(e) => onChange("cgst", Number(e.target.value) || 0)}
          data-testid={`${testidPrefix}-cgst`}
          placeholder="CGST + SGST %"
          className="w-full px-2 py-1.5 pr-20 bg-canvas border border-canvas-line text-xs font-mono focus:outline-none focus:border-ink"
        />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-subtle text-[10px]">
          CGST+SGST %
        </span>
      </div>
    </div>
  );
}

function ReceiptBucketGstFields({ form, onChange }) {
  return (
    <div className="border border-canvas-line p-3 space-y-2 bg-canvas-panel">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
        Per-Receipt GST (Helmet / Accessories)
      </div>
      <BucketGstRow
        label="Helmet"
        igstPct={form.helmet_igst_pct}
        cgstPct={form.helmet_cgst_pct}
        onChange={(k, v) =>
          onChange(k === "igst" ? { helmet_igst_pct: v } : { helmet_cgst_pct: v })
        }
        testidPrefix="package-helmet-gst"
      />
      <BucketGstRow
        label="Accessories"
        igstPct={form.accessories_igst_pct}
        cgstPct={form.accessories_cgst_pct}
        onChange={(k, v) =>
          onChange(
            k === "igst" ? { accessories_igst_pct: v } : { accessories_cgst_pct: v },
          )
        }
        testidPrefix="package-accessories-gst"
      />
      <div className="font-mono text-[10px] text-ink-subtle leading-tight">
        Computed at print time from each receipt&apos;s subtotal. For intra-state, the CGST+SGST rate
        is charged on each leg. For inter-state, IGST is the single tax. Ex-showroom GST lives
        in the Vehicle Pricing block above.
      </div>
    </div>
  );
}

export default function PackageFormDrawer({ open, editing, onClose, onSaved, defaultBranchTypeId }) {
  const [form, setForm] = useState(EMPTY_PACKAGE);
  const [saving, setSaving] = useState(false);
  const { types: branchTypes } = useBranchTypes({ activeOnly: true });

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        name: editing.name,
        price: editing.price,
        description: editing.description || "",
        items: (editing.items || []).map((it) => ({
          label: it.label,
          price: Number(it.price) || 0,
          optional: !!it.optional,
          note: it.note || "",
          receipt_bucket:
            it.receipt_bucket || BUCKET_DEFAULT_FOR_LABEL[it.label] || "other",
        })),
        active: editing.active !== false,
        igst_pct: Number(editing.igst_pct) || 0,
        cgst_pct: Number(editing.cgst_pct) || 0,
        helmet_igst_pct: Number(editing.helmet_igst_pct) || 0,
        helmet_cgst_pct: Number(editing.helmet_cgst_pct) || 0,
        accessories_igst_pct: Number(editing.accessories_igst_pct) || 0,
        accessories_cgst_pct: Number(editing.accessories_cgst_pct) || 0,
        branch_type_id: editing.branch_type_id || "general",
        model_category: (editing.model_category || "").toUpperCase(),
        company_discount: Number(editing.company_discount) || 0,
        dealer_margin: Number(editing.dealer_margin) || 0,
        mandatory_margin: Number(editing.mandatory_margin) || 0,
        helmet_margin: Number(editing.helmet_margin) || 0,
        add_acc_margin: Number(editing.add_acc_margin) || 0,
      });
    } else {
      setForm({ ...EMPTY_PACKAGE, branch_type_id: defaultBranchTypeId || "general" });
    }
  }, [open, editing, defaultBranchTypeId]);

  const itemsSum = useMemo(
    () => form.items.reduce((acc, it) => acc + (Number(it.price) || 0), 0),
    [form.items]
  );
  const usesItemPricing = itemsSum > 0;

  const confirm = useConfirm();
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await confirm({ title: "Save package?", body: "Package master will be updated. Active bookings/inventory using this package will see the new price." }))) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: usesItemPricing ? itemsSum : Number(form.price) || 0,
        company_discount: Number(form.company_discount) || 0,
        dealer_margin: Number(form.dealer_margin) || 0,
        mandatory_margin: Number(form.mandatory_margin) || 0,
        helmet_margin: Number(form.helmet_margin) || 0,
        add_acc_margin: Number(form.add_acc_margin) || 0,
      };
      if (editing) {
        // Check how many unsold vehicles will be impacted
        try {
          const { data } = await api.get(`/packages/${editing.id}/usage`);
          const unsold = data.unsold_vehicle_count || 0;
          if (unsold > 0) {
            const msg = `Saving will update on-road pricing for ${unsold} unsold vehicle${
              unsold === 1 ? "" : "s"
            } that use this package. Continue?`;
            // eslint-disable-next-line no-alert
            if (!window.confirm(msg)) {
              setSaving(false);
              return;
            }
          }
        } catch (usageErr) {
          // Non-fatal — usage check is advisory; proceed with the save.
          devWarn("Package usage check failed; saving without confirmation prompt", usageErr);
        }
        await api.put(`/packages/${editing.id}`, payload);
        toast.success("Package updated");
      } else {
        await api.post("/packages", payload);
        toast.success("Package created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow={`${editing ? "Edit" : "New"} Package`}
      title={editing ? editing.name : "Create a bundle"}
      testIdClose="close-package-form"
    >
      <form onSubmit={handleSubmit} data-testid="package-form" className="space-y-4">
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
            Branch Type
          </label>
          <select
            data-testid="package-branch-type"
            value={form.branch_type_id}
            onChange={(e) => setForm({ ...form, branch_type_id: e.target.value })}
            disabled={!!editing}
            required
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink disabled:opacity-60"
          >
            {branchTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.code})
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-ink-subtle">
            {editing
              ? "Branch type cannot be changed after creation. Create a new package for a different type."
              : "Package name can repeat across types with different prices."}
          </p>
        </div>
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
            Model category
          </label>
          <input
            list="model-category-list"
            data-testid="package-model-category"
            value={form.model_category}
            onChange={(e) =>
              setForm({ ...form, model_category: e.target.value.toUpperCase() })
            }
            placeholder="SCOOTER / MOPED / MOTORCYCLE / PREMIUM / SUPER PREMIUM / ELECTRIC …"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink uppercase"
          />
          <datalist id="model-category-list">
            {MODEL_CATEGORY_OPTIONS.map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>
          <p className="mt-1 text-[11px] text-ink-subtle">
            High-level classification used by reports & filters. Stored upper-case;
            pick a suggested value or type your own.
          </p>
        </div>
        <FormField
          label="Name"
          value={form.name}
          onChange={(v) => setForm({ ...form, name: v })}
          testId="package-name"
          required
        />
        {usesItemPricing ? (
          <div className="px-3 py-2 border border-canvas-line bg-canvas-panel">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
              Price (auto from items)
            </div>
            <div className="font-mono text-sm font-semibold text-ink" data-testid="package-calc-price">
              {fmtMoney(itemsSum)}
            </div>
          </div>
        ) : (
          <FormField
            label="Price (₹) — no priced items yet"
            type="number"
            value={form.price}
            onChange={(v) => setForm({ ...form, price: v })}
            testId="package-price"
          />
        )}
        <FormTextarea
          label="Description"
          value={form.description}
          onChange={(v) => setForm({ ...form, description: v })}
          testId="package-description"
        />

        <FormField
          label="Company Discount (₹) — OEM rebate auto-filled on sale"
          type="number"
          value={form.company_discount}
          onChange={(v) => setForm({ ...form, company_discount: v })}
          testId="package-company-discount"
        />

        <div
          className="rounded-md border border-dashed border-amber-500/50 bg-amber-50/40 dark:bg-amber-900/10 p-4 space-y-3"
          data-testid="package-margins-section"
        >
          <div className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            Margins &amp; Profit (Profit-Per-Vehicle Report)
          </div>
          <div className="text-xs text-muted-foreground">
            All values in ₹. <b>Dealer Margin</b> = Ex-showroom (ex-GST) − Purchase cost (ex-GST). Snapshotted onto each sale at creation
            — later edits do NOT retro-change past sales. (Insurance
            paid comes from the Registration's insurance-stage amount;
            insurance charged is derived from the <i>Insurance</i> package line-item
            price. Excess payments &amp; write-offs are pulled per-sale.)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormField
              label="Dealer Margin (₹)"
              type="number"
              value={form.dealer_margin}
              onChange={(v) => setForm({ ...form, dealer_margin: v })}
              testId="package-dealer-margin"
            />
            <FormField
              label="Mandatory Margin (₹)"
              type="number"
              value={form.mandatory_margin}
              onChange={(v) => setForm({ ...form, mandatory_margin: v })}
              testId="package-mandatory-margin"
            />
            <FormField
              label="Helmet Margin (₹)"
              type="number"
              value={form.helmet_margin}
              onChange={(v) => setForm({ ...form, helmet_margin: v })}
              testId="package-helmet-margin"
            />
            <FormField
              label="Add. Accessories Margin (₹)"
              type="number"
              value={form.add_acc_margin}
              onChange={(v) => setForm({ ...form, add_acc_margin: v })}
              testId="package-add-acc-margin"
            />
          </div>
        </div>

        <TaxFields
          exShowroom={
            form.items.find((it) => it.label === EX_SHOWROOM_LABEL)?.price || 0
          }
          igstPct={form.igst_pct}
          cgstPct={form.cgst_pct}
          onChange={(patch) => setForm({ ...form, ...patch })}
        />

        <ReceiptBucketGstFields
          form={form}
          onChange={(patch) => setForm({ ...form, ...patch })}
        />

        <ItemsEditor items={form.items} onChange={(items) => setForm({ ...form, items })} />

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            data-testid="package-active"
            checked={form.active}
            onChange={(e) => setForm({ ...form, active: e.target.checked })}
            className="accent-ink"
          />
          <span className="text-sm text-ink">Active (visible when attaching to vehicles)</span>
        </label>

        <div className="pt-4 flex items-center gap-2 justify-end border-t border-canvas-line -mx-6 px-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 border border-canvas-line text-sm hover:border-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="save-package-button"
            className="px-4 py-2.5 bg-ink text-white text-sm font-semibold hover:bg-brand transition-colors disabled:opacity-60"
          >
            {getSaveLabel(saving, editing, "Saving…", "Save changes", "Create package")}
          </button>
        </div>
      </form>
    </Drawer>
  );
}
